import type { ReactNode } from 'react'
import Button from './Button'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { appSignupLink } from '@/utils/signupUrl'
import { trackSignupCtaClick, type SignupLocation } from '@/utils/rum'

export interface SignupCtaProps {
  /** RUM `location` field: describes where on the site this CTA sits. */
  location: SignupLocation
  /** Optional plan slug to pre-select when the user lands on /sign-up. */
  plan?: string
  /** Visual variant. Defaults to primary. */
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  children: ReactNode
}

/**
 * Single source of truth for "Start Free Trial"-style buttons. Wraps Button
 * with:
 *   - the env-driven destination (`appSignupLink`)
 *   - URL parameter passthrough (`useUtmPassthrough`)
 *   - RUM telemetry (`trackSignupCtaClick`)
 *
 * Use anywhere the brochure has a signup-driving CTA, instead of a raw
 * `<Button href="/sign-up">…</Button>`.
 */
export default function SignupCta({
  location,
  plan,
  variant = 'primary',
  size = 'lg',
  className,
  children,
}: SignupCtaProps) {
  const utm = useUtmPassthrough()
  const link = appSignupLink({ plan, params: utm })

  const handleClick = () =>
    trackSignupCtaClick({ location, plan, hasUtm: Object.keys(utm).length > 0 })

  return (
    <Button
      href={link.href}
      external={link.external}
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
    >
      {children}
    </Button>
  )
}
